/**
 * Persisted record shapes and the schema version (SPEC §8.3, §10).
 *
 * This file is the vocabulary of everything that survives a reload. It holds no logic and imports
 * nothing but types, so the pure modules (`scheduler/`, `lang/`, `round/`, `tracker/metrics`) may
 * `import type` from it without acquiring a runtime dependency on `store/` — see DECISIONS D-024.
 *
 * Anything added here must also be added to the export schema (T75), because the brief's learner
 * is going to analyse this data himself and a field that exists but never exports is a lie by
 * omission.
 */

import type { ModeId, Tier } from '../state/gamestate';

/**
 * Bumped whenever a persisted shape changes meaning. Telemetry definitions are frozen from first
 * launch (SPEC §9) so that weeks 1-6 stay comparable; a bump here is the audit trail for a change
 * that breaks that comparability, and requires a DECISIONS.md entry.
 */
export const SCHEMA_VERSION = 1;

// ---------------------------------------------------------------------------------------------
// Scheduler memory
// ---------------------------------------------------------------------------------------------

export type MemoryState = 'new' | 'learning' | 'review' | 'relearning';

/**
 * FSRS memory state for one item (SPEC §7). Persisted, and consumed by the pure scheduler.
 *
 * `dueAt` is epoch milliseconds from the injected clock — never `Date.now()`.
 */
export interface Memory {
  /** Days. FSRS stability: the interval at which retrievability decays to `requestRetention`. */
  stability: number;
  /** FSRS difficulty, clamped to [1, 10] in exactly one place (T19d). */
  difficulty: number;
  reps: number;
  lapses: number;
  lastReviewAt: number | null;
  dueAt: number;
  state: MemoryState;
}

/** A brand-new item that has never been reviewed. `dueAt` is set when it is introduced. */
export function newMemory(dueAt: number): Memory {
  return {
    stability: 0,
    difficulty: 0,
    reps: 0,
    lapses: 0,
    lastReviewAt: null,
    dueAt,
    state: 'new',
  };
}

// ---------------------------------------------------------------------------------------------
// Attempts
// ---------------------------------------------------------------------------------------------

/**
 * How a wrong-but-close answer was wrong. Feedback names the error type rather than saying
 * "wrong" (BRIEF <spanish_correctness> layer 2). Classified by `lang/nearmiss.ts` (T33).
 */
export type NearMissKind =
  | 'missing-accent'
  | 'wrong-accent'
  | 'wrong-gender'
  | 'wrong-number'
  | 'wrong-person'
  | 'wrong-tense'
  | 'ser-estar'
  | 'spelling'
  | 'unrelated';

/**
 * One graded response. Written the instant it completes, before feedback renders (SPEC §10).
 *
 * `advancedInterval` mirrors `advancesInterval()` (SPEC §7) so invariants 1 and 2 are auditable
 * directly from the export rather than only from a passing test.
 */
export interface Attempt {
  id: string;
  itemId: string;
  tier: Tier;
  mode: ModeId;
  /** Epoch ms, from the injected clock. */
  at: number;
  correct: boolean;
  /** A scaffold was revealed. Invariant 2: this attempt does not advance the interval. */
  hinted: boolean;
  /** Not the first attempt on this item in this run. Invariant 1: does not satisfy the due date. */
  isRepeatWithinRun: boolean;
  /**
   * The draw was a due review, as opposed to a new-item intake or a discretionary practice draw.
   *
   * Required, with no default, because the 25% grammar cap turns on it: a due review is exempt
   * from displacement (SPEC §6.3, DECISIONS D-018) while still counting toward the reported share.
   * A default of `false` would silently mark reviews as discretionary and let the cap displace
   * them, which invariant 7 forbids; a default of `true` would let discretionary grammar draws
   * escape the cap entirely. Neither default is safe, so there is none.
   */
  wasDueReview: boolean;
  latencyMs: number;
  nearMiss: NearMissKind | null;
  advancedInterval: boolean;
  runId: string;
}

// ---------------------------------------------------------------------------------------------
// Manual logging
// ---------------------------------------------------------------------------------------------

/** The four plan components the game cannot observe (SPEC §8.4). */
export type ManualComponent = 'course' | 'input' | 'conversation' | 'narration';

export const MANUAL_COMPONENTS: readonly ManualComponent[] = [
  'course',
  'input',
  'conversation',
  'narration',
];

/**
 * A manually logged component. `minutes` is the only required field — a form with more required
 * fields than that does not get filled in at 3:40 a.m.
 *
 * These minutes are NEVER summed with `SessionRecord.appMeasuredMinutes` (DoD-12). They are
 * different claims: one is measured, one is remembered.
 */
export interface ManualEntry {
  id: string;
  component: ManualComponent;
  minutes: number;
  at: number;
  /** Conversation: free text for repeat errors, which becomes candidate items (DoD-11). */
  note?: string;
  derivedItemIds?: string[];
}

// ---------------------------------------------------------------------------------------------
// Sessions
// ---------------------------------------------------------------------------------------------

export interface SessionRecord {
  runId: string;
  startedAt: number;
  endedAt: number;
  /** Measured by the app. Never mixed with `ManualEntry.minutes`. */
  appMeasuredMinutes: number;
  itemsAttempted: number;
  itemsBanked: number;
}

// ---------------------------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------------------------

/** Build-time LLM vetting result (BRIEF <spanish_correctness> layer 1). A filter, not authority. */
export interface Verdict {
  model: string;
  /** ISO-8601 date the verdict was recorded. */
  date: string;
  grammatical: boolean;
  natural: boolean;
  mexican: boolean;
  register: string;
  correction?: string;
}

export type ItemSource = 'seed' | 'conversation-log' | 'conversion-rule';

export interface ItemRecord {
  id: string;
  tier: Tier;
  es: string;
  en: string;
  modes: ModeId[];
  tags: string[];
  /**
   * The Diana gate. ALWAYS false when generated, regardless of the LLM verdict — machine vetting
   * is a filter, not authority (BRIEF <spanish_correctness>). Only a human flips this.
   */
  vetted: boolean;
  verdict: Verdict | null;
  source: ItemSource;
  memory: Memory;
  /**
   * When this item was **shown** to the learner for the first time (T59c). Epoch ms.
   *
   * Recorded rather than inferred, and that distinction is the whole point. It was previously
   * derived from "is there an attempt for this item, or has its memory left `new`" — both of which
   * are true of an item the learner met *before the first-exposure step existed*, so every item in
   * an established store was permanently un-introduced. Reported from a real play session: a cloze
   * asking for `abajo` in "Te espero ___", having never once shown him the word.
   *
   * `undefined` means "not yet shown", which is exactly what an older record says about itself, so
   * the migration is the absence of the field. See DECISIONS D-213.
   */
  introducedAt?: number;
  /** Self-reference encoding written after the second failure (invariant 5). Surfaced on later ones. */
  encodingSentence?: string;
  failureCount: number;
}

// ---------------------------------------------------------------------------------------------
// Key space
// ---------------------------------------------------------------------------------------------

/**
 * One flat keyspace with type prefixes, so `list(prefix)` is the only query the store needs to
 * support. IndexedDB indexes and a query language would be a lot of machinery for a dataset that
 * is a few thousand rows at its largest.
 */
export const KEY = {
  item: (id: string) => `item/${id}`,
  attempt: (id: string) => `attempt/${id}`,
  manual: (id: string) => `manual/${id}`,
  session: (runId: string) => `session/${runId}`,
  meta: (name: string) => `meta/${name}`,
} as const;

export const PREFIX = {
  item: 'item/',
  attempt: 'attempt/',
  manual: 'manual/',
  session: 'session/',
  meta: 'meta/',
} as const;
